// The regions alone: this module must not pull MapLibre (and its worker) into the bundle on its own.
import { DALE_MAP_REGIONS, type MapCountry } from '@dale/map/style';

/**
 * Address lookup over OpenStreetMap through Photon (komoot), which allows
 * search-as-you-type and needs no key. Results stay inside the zone's country
 * and lean towards the zone itself. Pairs with Dale's own map (@dale/map).
 */

const PHOTON = 'https://photon.komoot.io';
const LIMIT = 5;

type PhotonFeature = {
  properties: Record<string, string | number | undefined>;
  geometry: { coordinates: [number, number] };
};

export type AddressHit = {
  id: string;
  /** Short first line: the named place or the street with its number. */
  name: string;
  /** The rest of the address, for telling two hits apart. */
  detail: string;
  /** What lands in the Dirección field. */
  label: string;
  lat: number;
  lng: number;
};

const text = (value: string | number | undefined) => (value === undefined ? '' : String(value).trim());

export function hitFromFeature(feature: PhotonFeature): AddressHit {
  const p = feature.properties;
  const street = [text(p.street), text(p.housenumber)].filter(Boolean).join(' ');
  const name = text(p.name) || street || text(p.locality) || text(p.city);
  const area = [text(p.locality), text(p.district) || text(p.city)].filter((part, index, all) => part && part !== name && all.indexOf(part) === index);
  const detail = area.join(', ');
  const [lng, lat] = feature.geometry.coordinates;
  return { id: `${text(p.osm_type)}${text(p.osm_id)}`, name, detail, label: detail ? `${name}, ${detail}` : name, lat, lng };
}

async function photon(path: string, params: Record<string, string>): Promise<PhotonFeature[]> {
  const url = new URL(path, PHOTON);
  for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`photon_${response.status}`);
  const body = (await response.json()) as { features?: PhotonFeature[] };
  return body.features ?? [];
}

/** Up to five places matching what was typed, in the country, closest to the zone first. */
/**
 * Address hits in `country` (ISO code). In Panamá and Venezuela the search is
 * fenced to Dale's map region and biased to the zone; anywhere else (2 Nov
 * 2026) it searches the whole map, biased to `near` when there is one, and
 * keeps only hits in that country.
 */
export async function searchAddress(
  query: string,
  near: readonly [number, number] | undefined,
  country: string,
): Promise<AddressHit[]> {
  const zoned = country === 'PA' || country === 'VE' ? (country as MapCountry) : undefined;
  const features = await photon('/api/', {
    q: query,
    ...(near ? { lat: String(near[0]), lon: String(near[1]) } : {}),
    ...(zoned ? { bbox: DALE_MAP_REGIONS[zoned].bounds.join(',') } : {}),
    limit: String(LIMIT + 3),
  });
  return features
    .filter((feature) => text(feature.properties.countrycode).toUpperCase() === country)
    .map(hitFromFeature)
    .filter((hit, index, all) => all.findIndex((other) => other.label === hit.label) === index)
    .slice(0, LIMIT);
}

/** The address under a tapped point, or null when OpenStreetMap has none there. */
export async function reverseAddress(lat: number, lng: number): Promise<string | null> {
  const [feature] = await photon('/reverse', { lat: String(lat), lon: String(lng), limit: '1' });
  return feature ? hitFromFeature(feature).label : null;
}
